import {useEffect, useState} from "react";
import {get, filter, isEmpty, forEach, find, map, keys} from "lodash";
import {strtotime} from "locutus/php/datetime";

import EnhancedEcommerce from "grandus-lib/utils/ecommerce";
import TagManager from "grandus-lib/utils/gtag";

import styles from "./AddToCartButton.module.scss";
import useCart from "grandus-lib/hooks/useCart";
import {useRouter} from "next/router";
import {Alert} from "antd";
import {useTranslation} from "next-i18next";

const getGroupedVariants = (variants) => {
  const groupedVariants = {};

  forEach(variants, (variant) => {
    if (variant?.options) {
      forEach(variant?.options, (option) => {
        if (!groupedVariants[option?.variantOptionName]) {
          groupedVariants[option?.variantOptionName] = [];
        }

        if (
          !find(groupedVariants[option?.variantOptionName], {
            value: option?.value,
          })
        ) {
          groupedVariants[option?.variantOptionName].push({
            id: option?.variantOptionId,
            value: option?.value,
          });
        }
      });
    }
  });

  return groupedVariants;
};

const getProductsWithOptions = (variants) => {
  const productsWithOptions = {};

  forEach(variants, (variant) => {
    if (variant?.options) {
      let options = {};
      forEach(variant?.options, (option) => {
        options[option?.variantOptionName] = option?.value;
      });

      productsWithOptions[variant?.variantProductId] = {
        product: variant?.product,
        ...options,
      };
    }
  });

  return productsWithOptions;
};

const Variants = ({variants, setSelectedVariant, setAvailable}) => {
  const groupedVariants = getGroupedVariants(variants);
  const [selectedOptions, setSelectedOption] = useState({});

  if (isEmpty(groupedVariants)) {
    return null;
  }

  const handleOptionClick = (groupName, value) => {
    setSelectedOption({...selectedOptions, ...{[groupName]: value}});
  };

  useEffect(() => {
    setAvailable(true);
    if (keys(selectedOptions).length === keys(groupedVariants).length) {
      const possibleOptions = getProductsWithOptions(variants);
      const found = find(possibleOptions, {...selectedOptions});
      setSelectedVariant(found ? found?.product : null);
      if (!found) {
        setAvailable(false);
      }
    }
  }, [selectedOptions, setSelectedVariant, groupedVariants]);

  return (
    <>
      {map(groupedVariants, (options, groupName) => (
        <>
          <h4>{groupName}</h4>
          <div className={styles.options}>
            {map(options, (option) => (
              <div
                onClick={() => handleOptionClick(groupName, option?.value)}
                className={`${styles.option} ${
                  selectedOptions[groupName] === option?.value
                    ? styles.active
                    : ""
                }`}
              >
                {option?.value}
              </div>
            ))}
          </div>
        </>
      ))}
    </>
  );
};


const AddToCartButton = ({product, options = {}, show = true}) => {
  const {variants} = product;
  const { t } = useTranslation();

  const router = useRouter();
  const {itemAdd} = useCart();

  const [count, setCount] = useState(1);
  const [store, setStore] = useState(get(product, "store.[0].id"));
  const [available, setAvailable] = useState(get(product, "availability"));
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);

  useEffect(() => {
    if (!isEmpty(selectedVariant)) {
      setStore(get(selectedVariant, "store.[0].id"));
    }
  }, [selectedVariant]);

  const selectedProduct = selectedVariant || product;

  if (!selectedProduct?.store.length) return null;

  async function handleAddToCart(e) {
    setLoading(true);
    setErrors([]);
    const requestOptions = {};
    if (router?.query?.hash) {
      requestOptions.hash = get(router, "query.hash");
    }

    itemAdd(
      count,
      store,
      selectedProduct?.id,
      (response) => {
        if (response?.success) {
          TagManager.push(
            EnhancedEcommerce.cartAdd(get(options, "eecProduct"), count)
          );
        } else if (!response?.success && !isEmpty(response?.messages)) {
          const cartErrors = [];
          forEach(get(response, "messages", []), (error) => {
            cartErrors.push(error?.message);
          });
          setErrors(cartErrors);
        }
        setLoading(false);
      },
      requestOptions
    );
    return false;
  }

  const totalPrice =
    count * get(selectedProduct, "finalPriceData.price") +
    get(selectedProduct, "finalPriceData.currencySymbol");

  let filteredStores = filter(selectedProduct?.store, function (store) {
    var date = strtotime(store.name);
    if (!date) {
      return true;
    }
    var now = strtotime("now");
    if (date > now) {
      return true;
    }
    return false;
  });

  return show
    ? (
      <div className={styles.addToCart}>
        <Variants
          variants={product?.variants}
          setSelectedVariant={setSelectedVariant}
          setAvailable={setAvailable}
        />
        {isEmpty(product?.variants) ? (
          <div className={styles.options}>
            {filteredStores.map((storeOption, index) => (
              <div
                key={`product-${selectedProduct?.id}-store-${get(
                  storeOption,
                  "id"
                )}-option-${index}`}
                onClick={function () {
                  setStore(get(storeOption, "id"));
                }}
                className={`${styles.option} ${
                  store == storeOption.id ? styles.active : ""
                }`}
              >
                {get(storeOption, "name")}
              </div>
            ))}
          </div>
        ) : null}

        {available ? (
          <>
            <div className={styles.counter}>
              <div className={styles.title}>{t("Počet")}</div>
              <input
                type={`number`}
                step={`1`}
                value={count}
                onChange={function (e) {
                  setCount(e.target.value);
                }}
              />
            </div>

            <div className={styles.total}>
              <div className={styles.title}>{t("Cena spolu")}: {totalPrice}</div>
              <small>
                {t("Jednotková cena")}:{" "}
                {get(selectedProduct, "finalPriceData.priceFormatted")}
              </small>
            </div>
            <div className={styles.button}>
              <>
                {!isEmpty(errors) ? (
                  <>
                    {map(errors, (error, index) => (
                      <Alert
                        key={`cart-error-${index}`}
                        type="error"
                        style={{marginBottom: "5px"}}
                        message={error}
                      />
                    ))}
                  </>
                ) : null}

                {(!isEmpty(variants) && !isEmpty(selectedVariant)) ||
                isEmpty(variants) ? (
                  <button
                    onClick={(e) => handleAddToCart(e)}
                    className="btn btn-white"
                  >
                    {loading ? t("Pracujem…") : t("Pridať do košíka")}
                  </button>
                ) : (
                  <button disabled={true} className="btn btn-white">
                    {t("Najprv vyberte variant")}
                  </button>
                )}
              </>
            </div>
          </>
        ) : (
          <Alert
            key={`availability-error`}
            type="error"
            message={
              t("Požadovaný produkt alebo zvolená kombinácie nie je dostupná")
            }
          />
        )}
      </div>
    )
    : (
      ""
    );
};

export default AddToCartButton;
